import React, { useEffect, useState } from 'react';
import { fetchProducts } from './api';
import { fetchOffers } from './offersApi';
import { fetchBranchById } from './branchesApi';
import { Box, Typography, List, ListItem, ListItemAvatar, Avatar, ListItemText, Divider, Chip, useTheme, alpha, Fade } from '@mui/material';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import { useCurrency } from './CurrencyContext';
import { getCurrencyDecimals } from './currencyHelpers';
import config from './config';
import { getImageUrls } from './getImageUrls';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { io } from 'socket.io-client';
import { useTranslation } from 'react-i18next';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const getItems = (order) => {
  if (order?.items && Array.isArray(order.items) && order.items.length > 0) return order.items;
  if (order?.OrderItemsJson) { try { const parsed = JSON.parse(order.OrderItemsJson); if (Array.isArray(parsed)) return parsed; } catch {} }
  return [];
};

const MapUpdater = ({ driverLocation, userLocation, branchLocation }) => {
  const map = useMap();
  useEffect(() => {
    const points = [];
    if (driverLocation) points.push(driverLocation);
    if (userLocation) points.push(userLocation);
    if (branchLocation) points.push(branchLocation);
    if (points.length > 1) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50] });
    } else if (points.length === 1) {
      map.panTo(points[0]);
    }
  }, [driverLocation, userLocation, branchLocation, map]);
  return null;
};

const OrderDeliveryPickup = ({ order, stageName }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const items = getItems(order);
  const [productMap, setProductMap] = useState({});
  const [driverLocation, setDriverLocation] = useState(null);
  const [branchLocation, setBranchLocation] = useState(null);
  const [socket, setSocket] = useState(null);

  const { getCurrencySymbol } = useCurrency();

  // ✅ Use ONLY order currency
  const orderCurrency = order?.Currency || 'YER';
  const currencySymbol = getCurrencySymbol(orderCurrency);
  const decimals = getCurrencyDecimals(orderCurrency);
  
  const formatPrice = (amount) => {
    const num = Number(amount || 0);
    return `${num.toFixed(decimals)} ${currencySymbol}`;
  };

  const isPreOrderDineIn = order?.IsPreOrderDineIn || order?.isPreOrderDineIn;
  const isDelivery = !isPreOrderDineIn;
  const userLat = order?.Latitude || order?.latitude || order?.DeliveryLatitude || order?.deliveryLatitude;
  const userLng = order?.Longitude || order?.longitude || order?.DeliveryLongitude || order?.deliveryLongitude;
  const userLocation = userLat && userLng ? [userLat, userLng] : null;

  useEffect(() => {
    const driver = order?.driver;
    const initialDriverLat = order?.DriverLatitude || order?.driverLatitude || driver?.latitude || driver?.Latitude;
    const initialDriverLng = order?.DriverLongitude || order?.driverLongitude || driver?.longitude || driver?.Longitude;
    if (initialDriverLat && initialDriverLng) setDriverLocation([parseFloat(initialDriverLat), parseFloat(initialDriverLng)]);
  }, [order]);

  useEffect(() => {
    const fetchBranch = async () => {
      const branchId = order?.BranchId || order?.branchId;
      if (branchId) {
        try {
          const branch = await fetchBranchById(branchId);
          if (branch && branch.latitude && branch.longitude) setBranchLocation([branch.latitude, branch.longitude]);
        } catch (error) {
          console.error("Failed to fetch branch location", error);
        }
      }
    };
    fetchBranch();
  }, [order]);

  useEffect(() => {
    if (isDelivery && order?.OrderId) {
      const SERVER_URL = config.API_BASE_URL.replace(/\/api$/, '');
      const newSocket = io(SERVER_URL, { path: '/api/socket.io', transports: ['websocket', 'polling'], autoConnect: true });
      newSocket.on('connect', () => { console.log('Socket.IO Connected'); newSocket.emit('JoinOrderGroup', order.OrderId.toString()); });
      newSocket.on('ReceiveLocation', (lat, long) => { console.log('Location received:', lat, long); setDriverLocation([lat, long]); });
      setSocket(newSocket);
      return () => { newSocket.disconnect(); };
    }
  }, [isDelivery, order?.OrderId]);

  useEffect(() => {
    let mounted = true;
    Promise.all([fetchProducts(), fetchOffers()]).then(([products, offers]) => {
      if (!mounted) return;
      const allOfferProducts = (offers || []).flatMap(offer => (offer.products || []).map(prod => ({ 
        ...prod, 
        Id: `${offer.offerCategoryId}_${prod.id}`, 
        ProductId: prod.id, 
        ProductName: prod.productName, 
        Price: prod.price, 
        OldPrice: prod.oldPrice, 
        ImageUrl: prod.imageJson, 
        ProductImage: prod.imageJson, 
        Description: prod.description, 
        categoryName: offer.categoryName, 
        discount: offer.discount,
        // ✅ Include ProductColors
        ProductColors: prod.ProductColors || prod.productColors || []
      })));
      const allProducts = [...products, ...allOfferProducts];
      const map = {};
      allProducts.forEach(p => { 
        map[String(p.Id)] = p; 
        if (p.ProductCode) map[String(p.ProductCode)] = p;
        if (p.ProductId) map[String(p.ProductId)] = p;
      });
      setProductMap(map);
    });
    return () => { mounted = false; };
  }, []);

  const driverIcon = new L.DivIcon({ className: 'custom-icon', html: '<div style="font-size: 32px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">🛵</div>', iconSize: [32, 32], iconAnchor: [16, 16] });
  const userIcon = new L.DivIcon({ className: 'custom-icon', html: '<div style="font-size: 32px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">🏠</div>', iconSize: [32, 32], iconAnchor: [16, 16] });
  const branchIcon = new L.DivIcon({ className: 'custom-icon', html: '<div style="font-size: 32px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">🏪</div>', iconSize: [32, 32], iconAnchor: [16, 16] });

  return (
    <Fade in timeout={600}>
      <Box sx={{ bgcolor: alpha(theme.palette.background.paper, 0.95), backdropFilter: 'blur(12px)', borderRadius: '28px', boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`, p: { xs: 3, md: 4 }, m: '2rem auto', maxWidth: 700, display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: 'Cairo, sans-serif', border: `2px solid ${alpha(theme.palette.secondary.main, 0.2)}`, transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden', '&:hover': { transform: 'translateY(-8px)', boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.18)}` }, '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 6, background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})` } }}>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, position: 'relative', zIndex: 1 }}>
          <Box sx={{ position: 'relative', mb: 2 }}>
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.2)} 0%, transparent 70%)`, animation: 'pulse 2s infinite' }} />
            {isDelivery ? (
              <DeliveryDiningIcon sx={{ fontSize: 80, color: 'secondary.main', position: 'relative', zIndex: 1, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))', animation: 'slideRight 2s infinite' }} />
            ) : (
              <StorefrontIcon sx={{ fontSize: 80, color: 'secondary.main', position: 'relative', zIndex: 1, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))', animation: 'pulse 2s infinite' }} />
            )}
          </Box>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 900, mb: 1.5, color: 'secondary.main', textAlign: 'center', letterSpacing: '-0.5px' }}>{stageName || (isDelivery ? t('orderStages.deliveryPickup.defaultTitleDelivery') : t('orderStages.deliveryPickup.defaultTitlePickup'))}</Typography>
          <Chip label={isDelivery ? t('orderStages.deliveryPickup.statusDelivery') : t('orderStages.deliveryPickup.statusPickup')} color="secondary" sx={{ fontWeight: 700, fontSize: '0.95rem', px: 3, py: 2.5, borderRadius: '16px', boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}` }} />
        </Box>

        <Box sx={{ width: '100%', height: 340, borderRadius: '20px', overflow: 'hidden', mb: 3, boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.15)}`, border: `3px solid ${alpha(theme.palette.secondary.main, 0.2)}`, position: 'relative', zIndex: 1 }}>
          <MapContainer center={userLocation || branchLocation || [24.7136, 46.6753]} zoom={13} style={{ height: '100%', width: '100%' }} zoomControl={false} attributionControl={false}>
            <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
            {userLocation && <Marker position={userLocation} icon={userIcon}><Popup><strong>{t('driver.customerAddress')}</strong></Popup></Marker>}
            {driverLocation && <Marker position={driverLocation} icon={driverIcon}><Popup><strong>{t('driver.dashboard')}</strong></Popup></Marker>}
            {branchLocation && <Marker position={branchLocation} icon={branchIcon}><Popup><strong>{t('driver.restaurant')}</strong></Popup></Marker>}
            <MapUpdater driverLocation={driverLocation} userLocation={userLocation} branchLocation={branchLocation} />
          </MapContainer>
        </Box>

        <Box sx={{ width: '100%', bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: '20px', p: 3, mb: 3, border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="body1" fontWeight="700" color="text.secondary">{t('orderStages.common.orderNumber')}:</Typography>
            <Chip label={`#${order?.orderNumber || order?.OrderId || order?.orderId}`} size="small" sx={{ fontFamily: 'monospace', fontWeight: 800, bgcolor: 'background.paper', px: 2 }} />
          </Box>
          <Divider sx={{ my: 1.5, opacity: 0.6 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1" fontWeight="700" color="text.secondary">{t('orderStages.common.orderDate')}:</Typography>
            <Typography variant="body1" fontWeight="600">{new Date(order?.orderDate || order?.CreatedDate).toLocaleDateString('ar-EG')}</Typography>
          </Box>
        </Box>

        <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: '20px', p: 3, mb: 3, boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.06)}`, position: 'relative', zIndex: 1 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 900, borderBottom: '3px solid', borderColor: 'secondary.main', pb: 1.5, color: 'text.primary' }}>{t('orderStages.common.items')}</Typography>
          <List disablePadding>
            {items.length === 0 && <Typography color="text.secondary">{t('orderStages.common.noItems')}</Typography>}
            {items.map((item, idx) => {
              const code = item.ProductCode || item.ProductId || item.id || item.Id;
              let prod = productMap[String(code)] || {};
              
              // ✅ Enhanced product lookup
              if (Object.keys(prod).length === 0) {
                if (item.ProductId) {
                  prod = Object.values(productMap).find(p => 
                    String(p.ProductId) === String(item.ProductId) ||
                    String(p.Id) === String(item.ProductId)
                  ) || {};
                }
                
                if (Object.keys(prod).length === 0 && (item.name || item.ProductName)) {
                  const searchName = (item.name || item.ProductName).toLowerCase();
                  prod = Object.values(productMap).find(p => 
                    (p.ProductName || p.Name || '').toLowerCase() === searchName
                  ) || {};
                }
              }
              
              // ✅ Merge item data with product data
              const mergedProduct = { ...prod, ...item };
              
              const name = item.ProductName || item.name || prod.ProductName || prod.Name || `Product ${code}`;
              const description = item.Description || item.description || prod.Description || prod.description || '';
              
              const { thumbnail: img } = getImageUrls(mergedProduct);
              
              const qty = item.quantity || item.Quantity || 1;
              
              // ✅ Use price directly from item
              const price = Number(item.Price || item.price || 0);
              const total = Number(qty) * price;
              
              // ✅ Get selected color
              const selectedColor = item.SelectedColor || item.selectedColor || null;
              
              return (
                <React.Fragment key={idx}>
                  <ListItem alignItems="flex-start" sx={{ px: 0, py: 2, '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.03) }, transition: 'background 0.2s', borderRadius: '12px' }}>
                    <ListItemAvatar>
                      <Avatar 
                        variant="rounded" 
                        src={img} 
                        alt={name} 
                        sx={{ 
                          width: 60, 
                          height: 60, 
                          mr: 2, 
                          border: `2px solid ${alpha(theme.palette.divider, 0.5)}`, 
                          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}` 
                        }}
                      >
                        {name?.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight="800" sx={{ mb: 0 }}>
                              {name}
                            </Typography>
                            <Chip 
                              label={`×${qty}`} 
                              size="small" 
                              sx={{ height: 20, fontSize: '0.75rem', fontWeight: 700 }} 
                            />
                          </Box>
                          
                          {/* ✅ Display Selected Color */}
                          {selectedColor && (
                            <Chip 
                              label={`🎨 ${selectedColor}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ 
                                height: 22, 
                                fontSize: '0.7rem', 
                                fontWeight: 600,
                                alignSelf: 'flex-start'
                              }}
                            />
                          )}
                          
                          {/* ✅ Display description if available */}
                          {description && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                fontSize: '0.75rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}
                            >
                              {description}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" fontWeight="600">
                          {formatPrice(price)} {t('orderStages.common.each', 'each')}
                        </Typography>
                      }
                    />
                    <Typography variant="subtitle1" fontWeight="900" color="secondary.main" sx={{ fontSize: '1.1rem' }}>
                      {formatPrice(total)}
                    </Typography>
                  </ListItem>
                  
                  {/* ✅ Custom Product Subproducts */}
                  {item.isCustom && Array.isArray(item.SubProducts) && item.SubProducts.length > 0 && (
                    <Box sx={{ px: 1, pb: 2, bgcolor: alpha(theme.palette.info.main, 0.03), borderRadius: '12px', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'info.main', mb: 1, fontSize: '0.85rem' }}>
                        {t('customProduct.selectedSubproducts', 'Selected Options')}:
                      </Typography>
                      <List dense disablePadding>
                        {item.SubProducts.map((sp, spIdx) => {
                          if (!sp.Name) return null;
                          
                          // ✅ Use price directly from subproduct
                          const spPrice = Number(sp.Price || sp.price || 0);
                          
                          return (
                            <ListItem key={sp.Id || spIdx} sx={{ pl: 0, py: 0.5 }}>
                              <ListItemText primary={
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                  <Typography variant="body2" fontWeight="700" sx={{ fontSize: '0.85rem' }}>
                                    {sp.Name}
                                  </Typography>
                                  {spPrice > 0 && (
                                    <Chip 
                                      label={formatPrice(spPrice)} 
                                      size="small" 
                                      color="primary" 
                                      variant="outlined" 
                                      sx={{ height: 20, fontSize: '0.7rem' }} 
                                    />
                                  )}
                                  {sp.Quantity && sp.Quantity !== 0 && (
                                    <Chip 
                                      label={`Qty: ${sp.Quantity}`} 
                                      size="small" 
                                      color="secondary" 
                                      variant="outlined" 
                                      sx={{ height: 20, fontSize: '0.7rem' }} 
                                    />
                                  )}
                                  {sp.SizeType && (
                                    <Chip 
                                      label={sp.SizeType} 
                                      size="small" 
                                      sx={{ height: 20, fontSize: '0.7rem', bgcolor: alpha(theme.palette.info.main, 0.1) }} 
                                    />
                                  )}
                                  {sp.Height && sp.Height !== 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      H: {sp.Height}
                                    </Typography>
                                  )}
                                  {sp.Width && sp.Width !== 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      W: {sp.Width}
                                    </Typography>
                                  )}
                                </Box>
                              } />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  )}
                  
                  {idx < items.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              );
            })}
          </List>
        </Box>

        {(order?.isDelivery || order?.Status === 'Out for Delivery') ? (
          <Box sx={{ width: '100%', bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: '20px', p: 3, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <DeliveryDiningIcon sx={{ fontSize: 28, color: 'success.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 900, color: 'success.main' }}>{t('orderStages.deliveryPickup.driverInfo')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, p: 2, bgcolor: 'background.paper', borderRadius: '12px' }}>
              <Typography variant="body1" fontWeight="700" color="text.secondary">{t('driver.personalInfo')}:</Typography>
              <Typography variant="body1" fontWeight="700">{order?.driver?.name || t('orderStages.deliveryPickup.driverAssigned')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'background.paper', borderRadius: '12px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ fontSize: 20, color: 'success.main' }} />
                <Typography variant="body1" fontWeight="700" color="text.secondary">{t('driver.phone')}:</Typography>
              </Box>
              <Chip label={order?.driver?.contact || t('orderStages.deliveryPickup.contactNotAvailable')} size="small" sx={{ fontFamily: 'monospace', fontWeight: 800 }} />
            </Box>
            {order?.driver?.location && (
              <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: '12px', textAlign: 'center', border: `1px dashed ${alpha(theme.palette.success.main, 0.3)}` }}>
                <LocationOnIcon sx={{ fontSize: 20, color: 'success.main', mb: 0.5 }} />
                <Typography variant="body2" fontWeight="600" color="success.main">{order.driver.location}</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ width: '100%', bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: '20px', p: 3, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <StorefrontIcon sx={{ fontSize: 28, color: 'info.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 900, color: 'info.main' }}>{t('orderStages.deliveryPickup.pickupInstructions')}</Typography>
            </Box>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>{order?.pickupInstructions || t('orderStages.deliveryPickup.defaultPickupInstructions')}</Typography>
            {order?.restaurantLocation && (
              <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: '12px', textAlign: 'center', border: `1px dashed ${alpha(theme.palette.info.main, 0.3)}` }}>
                <LocationOnIcon sx={{ fontSize: 20, color: 'info.main', mb: 0.5 }} />
                <Typography variant="body2" fontWeight="600" color="info.main">{order.restaurantLocation}</Typography>
              </Box>
            )}
          </Box>
        )}

        <Typography variant="body2" sx={{ mt: 3, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', px: 2, position: 'relative', zIndex: 1 }}>
          {t('orderStages.deliveryPickup.notificationSent')}
        </Typography>

        <style>{`
          @keyframes pulse { 
            0%, 100% { opacity: 1; transform: scale(1); } 
            50% { opacity: 0.8; transform: scale(1.05); } 
          }
          @keyframes slideRight { 
            0%, 100% { transform: translateX(0); } 
            50% { transform: translateX(8px); } 
          }
        `}</style>
      </Box>
    </Fade>
  );
};

export default OrderDeliveryPickup;